import {
  createEmptyLoomControlWorkspace,
  parseLoomControlWorkspace,
  parseLoomControlWorkspaceIntent,
  reduceLoomControlWorkspace,
  type LoomControlWorkspace,
  type LoomControlWorkspaceContext,
  type LoomControlWorkspaceIntent,
} from "./loomControlWorkspace";

export type LoomControlWorkspaceOperationOrigin = "assistant.ollama" | "gui.control";

export type LoomControlWorkspaceBootstrap =
  | { kind: "native-v2" }
  | { kind: "geometry-only-v2-migrated"; sourceSchema: "thread-loom-authoring-history-storage/v2" };

export interface LoomControlWorkspaceStateRecord {
  schema: "loom-control-workspace-state/v1";
  stateId: string;
  workspace: LoomControlWorkspace;
}

export interface LoomControlWorkspaceHead {
  stateId: string;
  revision: number;
}

interface LoomControlWorkspaceOperationBase {
  schema: "loom-control-workspace-operation/v1";
  operationId: string;
  sequence: number;
}

export interface LoomControlWorkspaceIntentOperation extends LoomControlWorkspaceOperationBase {
  kind: "intent.accepted";
  origin: LoomControlWorkspaceOperationOrigin;
  documentRevision: number;
  /** Absent only on replayed workspace operations written before state-aware provenance. */
  documentStateId?: string;
  selectedNodeId: string | null;
  parent: LoomControlWorkspaceHead;
  result: LoomControlWorkspaceHead;
  intent: LoomControlWorkspaceIntent;
  discardedRedoOperationIds: readonly string[];
}

export interface LoomControlWorkspaceHeadMovedOperation extends LoomControlWorkspaceOperationBase {
  kind: "head.moved";
  move: "undo" | "redo";
  from: LoomControlWorkspaceHead;
  to: LoomControlWorkspaceHead;
  transitionOperationId: string;
}

export type LoomControlWorkspaceOperation =
  | LoomControlWorkspaceIntentOperation
  | LoomControlWorkspaceHeadMovedOperation;

export interface LoomControlWorkspaceHistory {
  schema: "loom-control-workspace-history/v1";
  bootstrap: LoomControlWorkspaceBootstrap;
  initialHead: LoomControlWorkspaceHead;
  states: readonly LoomControlWorkspaceStateRecord[];
  operations: readonly LoomControlWorkspaceOperation[];
  activeBranchOperationIds: readonly string[];
  cursor: number;
  activeHead: LoomControlWorkspaceHead;
}

export interface LoomControlWorkspaceAcceptance {
  origin: LoomControlWorkspaceOperationOrigin;
  documentRevision: number;
  documentStateId: string;
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown, path: string): UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as UnknownRecord;
}

function exactKeys(value: UnknownRecord, allowed: readonly string[], path: string): void {
  const unknown = Object.keys(value).filter((key) => !allowed.includes(key));
  if (unknown.length > 0) throw new Error(`${path} contains unknown fields: ${unknown.join(", ")}`);
}

function integer(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${path} must be a nonnegative safe integer`);
  }
  return value;
}

function nonEmpty(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") throw new Error(`${path} must be a non-empty string`);
  return value;
}

function optionalString(value: unknown, path: string): string | null {
  return value === null ? null : nonEmpty(value, path);
}

function uniqueStrings(value: unknown, path: string): string[] {
  if (!Array.isArray(value)) throw new Error(`${path} must be an array`);
  const result = value.map((item, index) => nonEmpty(item, `${path}[${index}]`));
  if (new Set(result).size !== result.length) throw new Error(`${path} contains duplicate ids`);
  return result;
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value === "boolean" || typeof value === "string") return JSON.stringify(value);
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("canonical content contains a non-finite number");
    return Object.is(value, -0) ? "0" : JSON.stringify(value);
  }
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (typeof value === "object") {
    const item = value as UnknownRecord;
    const keys = Object.keys(item).sort();
    if (keys.some((key) => item[key] === undefined)) throw new Error("canonical content contains undefined");
    return `{${keys.map((key) => `${JSON.stringify(key)}:${canonicalJson(item[key])}`).join(",")}}`;
  }
  throw new Error(`canonical content contains unsupported ${typeof value}`);
}

function contentId(prefix: string, value: unknown): string {
  let hash = 0xcbf29ce484222325n;
  for (const byte of new TextEncoder().encode(canonicalJson(value))) {
    hash = BigInt.asUintN(64, (hash ^ BigInt(byte)) * 0x100000001b3n);
  }
  return `${prefix}-${hash.toString(16).padStart(16, "0")}`;
}

function immutable<T>(value: T): T {
  if (typeof value !== "object" || value === null || Object.isFrozen(value)) return value;
  Object.values(value as UnknownRecord).forEach(immutable);
  return Object.freeze(value);
}

function stateRecord(workspaceValue: unknown): LoomControlWorkspaceStateRecord {
  const workspace = parseLoomControlWorkspace(workspaceValue);
  return immutable({
    schema: "loom-control-workspace-state/v1",
    stateId: contentId("loom-workspace-state", workspace),
    workspace,
  });
}

function parseState(value: unknown, path: string): LoomControlWorkspaceStateRecord {
  const item = record(value, path);
  exactKeys(item, ["schema", "stateId", "workspace"], path);
  if (item.schema !== "loom-control-workspace-state/v1") throw new Error(`${path}.schema is unsupported`);
  const expected = stateRecord(item.workspace);
  if (item.stateId !== expected.stateId) throw new Error(`${path}.stateId does not match its content`);
  return expected;
}

function parseHead(value: unknown, path: string): LoomControlWorkspaceHead {
  const item = record(value, path);
  exactKeys(item, ["stateId", "revision"], path);
  return immutable({
    stateId: nonEmpty(item.stateId, `${path}.stateId`),
    revision: integer(item.revision, `${path}.revision`),
  });
}

function parseBootstrap(value: unknown): LoomControlWorkspaceBootstrap {
  const item = record(value, "workspaceHistory.bootstrap");
  if (item.kind === "native-v2") {
    exactKeys(item, ["kind"], "workspaceHistory.bootstrap");
    return { kind: "native-v2" };
  }
  if (item.kind === "geometry-only-v2-migrated") {
    exactKeys(item, ["kind", "sourceSchema"], "workspaceHistory.bootstrap");
    if (item.sourceSchema !== "thread-loom-authoring-history-storage/v2") {
      throw new Error("workspaceHistory.bootstrap.sourceSchema is unsupported");
    }
    return { kind: "geometry-only-v2-migrated", sourceSchema: item.sourceSchema };
  }
  throw new Error("workspaceHistory.bootstrap.kind is unsupported");
}

function sameHead(left: LoomControlWorkspaceHead, right: LoomControlWorkspaceHead): boolean {
  return left.stateId === right.stateId && left.revision === right.revision;
}

function operationContent(operation: LoomControlWorkspaceOperation): unknown {
  const base = { schema: operation.schema, sequence: operation.sequence, kind: operation.kind };
  return operation.kind === "intent.accepted"
    ? {
        ...base,
        origin: operation.origin,
        documentRevision: operation.documentRevision,
        ...(operation.documentStateId ? { documentStateId: operation.documentStateId } : {}),
        selectedNodeId: operation.selectedNodeId,
        parent: operation.parent,
        result: operation.result,
        intent: operation.intent,
        discardedRedoOperationIds: operation.discardedRedoOperationIds,
      }
    : {
        ...base,
        move: operation.move,
        from: operation.from,
        to: operation.to,
        transitionOperationId: operation.transitionOperationId,
      };
}

function sealOperation<T extends LoomControlWorkspaceOperation>(operation: T): T {
  return immutable({ ...operation, operationId: contentId("loom-workspace-operation", operationContent(operation)) });
}

function parseOperation(value: unknown, path: string): LoomControlWorkspaceOperation {
  const item = record(value, path);
  const baseKeys = ["schema", "operationId", "sequence", "kind"];
  if (item.schema !== "loom-control-workspace-operation/v1") throw new Error(`${path}.schema is unsupported`);
  const base = {
    schema: "loom-control-workspace-operation/v1" as const,
    operationId: nonEmpty(item.operationId, `${path}.operationId`),
    sequence: integer(item.sequence, `${path}.sequence`),
  };
  let operation: LoomControlWorkspaceOperation;
  if (item.kind === "intent.accepted") {
    exactKeys(item, [...baseKeys, "origin", "documentRevision", "documentStateId", "selectedNodeId", "parent", "result", "intent", "discardedRedoOperationIds"], path);
    if (item.origin !== "assistant.ollama" && item.origin !== "gui.control") throw new Error(`${path}.origin is unsupported`);
    operation = {
      ...base,
      kind: "intent.accepted",
      origin: item.origin,
      documentRevision: integer(item.documentRevision, `${path}.documentRevision`),
      ...(item.documentStateId === undefined ? {} : {
        documentStateId: nonEmpty(item.documentStateId, `${path}.documentStateId`),
      }),
      selectedNodeId: optionalString(item.selectedNodeId, `${path}.selectedNodeId`),
      parent: parseHead(item.parent, `${path}.parent`),
      result: parseHead(item.result, `${path}.result`),
      intent: parseLoomControlWorkspaceIntent(item.intent),
      discardedRedoOperationIds: uniqueStrings(item.discardedRedoOperationIds, `${path}.discardedRedoOperationIds`),
    };
  } else if (item.kind === "head.moved") {
    exactKeys(item, [...baseKeys, "move", "from", "to", "transitionOperationId"], path);
    if (item.move !== "undo" && item.move !== "redo") throw new Error(`${path}.move is unsupported`);
    operation = {
      ...base,
      kind: "head.moved",
      move: item.move,
      from: parseHead(item.from, `${path}.from`),
      to: parseHead(item.to, `${path}.to`),
      transitionOperationId: nonEmpty(item.transitionOperationId, `${path}.transitionOperationId`),
    };
  } else throw new Error(`${path}.kind is unsupported`);
  if (operation.operationId !== contentId("loom-workspace-operation", operationContent(operation))) {
    throw new Error(`${path}.operationId does not match its content`);
  }
  return immutable(operation);
}

function validateReplay(history: LoomControlWorkspaceHistory): void {
  const states = new Map(history.states.map((state) => [state.stateId, state]));
  if (states.size !== history.states.length || !states.has(history.initialHead.stateId)) {
    throw new Error("workspace history state index is invalid");
  }
  const accepted = new Map<string, LoomControlWorkspaceIntentOperation>();
  const operationIds = new Set<string>();
  let branch: string[] = [];
  let cursor = 0;
  let head = history.initialHead;
  history.operations.forEach((operation, sequence) => {
    if (operation.sequence !== sequence || operationIds.has(operation.operationId)) {
      throw new Error("workspace history operation sequence is invalid");
    }
    operationIds.add(operation.operationId);
    if (operation.kind === "intent.accepted") {
      if (!sameHead(operation.parent, head)) throw new Error("workspace intent does not start at the active head");
      if (operation.intent.expectedRevision !== operation.documentRevision) throw new Error("workspace intent revision audit is invalid");
      if (operation.result.revision !== operation.parent.revision + 1 || !states.has(operation.result.stateId)) {
        throw new Error("workspace intent result is invalid");
      }
      const discarded = branch.slice(cursor);
      if (canonicalJson(discarded) !== canonicalJson(operation.discardedRedoOperationIds)) {
        throw new Error("workspace intent branch metadata is invalid");
      }
      branch = [...branch.slice(0, cursor), operation.operationId];
      cursor += 1;
      head = operation.result;
      accepted.set(operation.operationId, operation);
    } else {
      if (!sameHead(operation.from, head)) throw new Error("workspace head move starts from the wrong head");
      const transitionId = operation.move === "undo" ? branch[cursor - 1] : branch[cursor];
      if (!transitionId || transitionId !== operation.transitionOperationId) throw new Error("workspace head move target is invalid");
      const transition = accepted.get(transitionId);
      if (!transition) throw new Error("workspace head move transition is missing");
      const target = operation.move === "undo" ? transition.parent : transition.result;
      if (!sameHead(operation.to, target)) throw new Error("workspace head move result is invalid");
      cursor += operation.move === "undo" ? -1 : 1;
      head = target;
    }
  });
  if (canonicalJson(branch) !== canonicalJson(history.activeBranchOperationIds)) {
    throw new Error("workspace active branch is invalid");
  }
  if (cursor !== history.cursor || !sameHead(head, history.activeHead)) throw new Error("workspace active head is invalid");
  const referencedStates = new Set([history.initialHead.stateId]);
  history.operations.forEach((operation) => {
    if (operation.kind === "intent.accepted") referencedStates.add(operation.result.stateId);
  });
  if (referencedStates.size !== states.size || history.states.some((state) => !referencedStates.has(state.stateId))) {
    throw new Error("workspace history contains an unreferenced state");
  }
}

export function parseLoomControlWorkspaceHistory(value: unknown): LoomControlWorkspaceHistory {
  const item = record(value, "workspaceHistory");
  exactKeys(item, ["schema", "bootstrap", "initialHead", "states", "operations", "activeBranchOperationIds", "cursor", "activeHead"], "workspaceHistory");
  if (item.schema !== "loom-control-workspace-history/v1") throw new Error("workspaceHistory.schema is unsupported");
  if (!Array.isArray(item.states) || !Array.isArray(item.operations)) throw new Error("workspaceHistory states and operations must be arrays");
  const history: LoomControlWorkspaceHistory = {
    schema: "loom-control-workspace-history/v1",
    bootstrap: parseBootstrap(item.bootstrap),
    initialHead: parseHead(item.initialHead, "workspaceHistory.initialHead"),
    states: item.states.map((state, index) => parseState(state, `workspaceHistory.states[${index}]`)),
    operations: item.operations.map((operation, index) => parseOperation(operation, `workspaceHistory.operations[${index}]`)),
    activeBranchOperationIds: uniqueStrings(item.activeBranchOperationIds, "workspaceHistory.activeBranchOperationIds"),
    cursor: integer(item.cursor, "workspaceHistory.cursor"),
    activeHead: parseHead(item.activeHead, "workspaceHistory.activeHead"),
  };
  if (history.cursor > history.activeBranchOperationIds.length) throw new Error("workspaceHistory.cursor exceeds active branch");
  validateReplay(history);
  return immutable(history);
}

export function createLoomControlWorkspaceHistory(
  bootstrap: LoomControlWorkspaceBootstrap = { kind: "native-v2" },
): LoomControlWorkspaceHistory {
  const state = stateRecord(createEmptyLoomControlWorkspace());
  return parseLoomControlWorkspaceHistory({
    schema: "loom-control-workspace-history/v1",
    bootstrap,
    initialHead: { stateId: state.stateId, revision: 0 },
    states: [state],
    operations: [],
    activeBranchOperationIds: [],
    cursor: 0,
    activeHead: { stateId: state.stateId, revision: 0 },
  });
}

export function currentLoomControlWorkspace(historyValue: unknown): LoomControlWorkspace {
  const history = parseLoomControlWorkspaceHistory(historyValue);
  const state = history.states.find(({ stateId }) => stateId === history.activeHead.stateId);
  if (!state) throw new Error("workspace active state is missing");
  return state.workspace;
}

export function acceptLoomControlWorkspaceIntent(
  historyValue: unknown,
  intentValue: unknown,
  context: LoomControlWorkspaceContext,
  acceptance: LoomControlWorkspaceAcceptance,
): LoomControlWorkspaceHistory {
  const history = parseLoomControlWorkspaceHistory(historyValue);
  const intent = parseLoomControlWorkspaceIntent(intentValue);
  nonEmpty(acceptance.documentStateId, "acceptance.documentStateId");
  if (intent.expectedRevision !== acceptance.documentRevision) {
    throw new Error(`revision conflict: expected ${intent.expectedRevision}, current ${acceptance.documentRevision}`);
  }
  const result = reduceLoomControlWorkspace(currentLoomControlWorkspace(history), intent, context).workspace;
  const state = stateRecord(result);
  const existingState = history.states.find(({ stateId }) => stateId === state.stateId);
  const draft: LoomControlWorkspaceIntentOperation = {
    schema: "loom-control-workspace-operation/v1",
    operationId: "pending",
    sequence: history.operations.length,
    kind: "intent.accepted",
    origin: acceptance.origin,
    documentRevision: acceptance.documentRevision,
    documentStateId: acceptance.documentStateId,
    selectedNodeId: context.selectedNodeId,
    parent: history.activeHead,
    result: { stateId: state.stateId, revision: history.activeHead.revision + 1 },
    intent,
    discardedRedoOperationIds: history.activeBranchOperationIds.slice(history.cursor),
  };
  const operation = sealOperation(draft);
  return parseLoomControlWorkspaceHistory({
    ...history,
    states: existingState ? history.states : [...history.states, state],
    operations: [...history.operations, operation],
    activeBranchOperationIds: [...history.activeBranchOperationIds.slice(0, history.cursor), operation.operationId],
    cursor: history.cursor + 1,
    activeHead: operation.result,
  });
}

function moveWorkspaceHead(historyValue: unknown, move: "undo" | "redo"): LoomControlWorkspaceHistory {
  const history = parseLoomControlWorkspaceHistory(historyValue);
  const transitionId = move === "undo"
    ? history.activeBranchOperationIds[history.cursor - 1]
    : history.activeBranchOperationIds[history.cursor];
  if (!transitionId) throw new Error(`workspace ${move} is unavailable`);
  const transition = history.operations.find((operation): operation is LoomControlWorkspaceIntentOperation => (
    operation.kind === "intent.accepted" && operation.operationId === transitionId
  ));
  if (!transition) throw new Error(`workspace ${move} transition is missing`);
  const target = move === "undo" ? transition.parent : transition.result;
  const operation = sealOperation<LoomControlWorkspaceHeadMovedOperation>({
    schema: "loom-control-workspace-operation/v1",
    operationId: "pending",
    sequence: history.operations.length,
    kind: "head.moved",
    move,
    from: history.activeHead,
    to: target,
    transitionOperationId: transitionId,
  });
  return parseLoomControlWorkspaceHistory({
    ...history,
    operations: [...history.operations, operation],
    cursor: history.cursor + (move === "undo" ? -1 : 1),
    activeHead: target,
  });
}

export function undoLoomControlWorkspaceHistory(historyValue: unknown): LoomControlWorkspaceHistory {
  return moveWorkspaceHead(historyValue, "undo");
}

export function redoLoomControlWorkspaceHistory(historyValue: unknown): LoomControlWorkspaceHistory {
  return moveWorkspaceHead(historyValue, "redo");
}
